import React from 'react';
import TopBar from './TopBar';
import { ELEMENTS } from '../utils/constants';
import { daysSinceLastEntry } from '../utils/helpers';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { getCycleSummary } from '../utils/cycle';

function Home({ state, setRoute }) {
  const today = new Date().toISOString().slice(0, 10);
  const todayEntry = state.entries.find(entry => entry.date === today);
  const { isMobile } = useDeviceDetection();
  const cycleSummary = getCycleSummary(state.cycleTracking);

  // Get the most recent entry excluding today's
  const previousEntry = state.entries.find(entry => entry.date !== today);

  return (
    <div className="p-4">
      <TopBar title={`Today • ${new Date().toDateString()}`} />
      <div className="mt-3">
        {cycleSummary.enabled && cycleSummary.cycleDay && (
          <div className="mb-4 p-4 rounded-xl bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-700">
            <div className="text-xs uppercase tracking-wide text-pink-700 dark:text-pink-200">Cycle snapshot</div>
            <div className="mt-2 text-lg font-semibold text-pink-800 dark:text-pink-100">
              Day {cycleSummary.cycleDay}{cycleSummary.totalLength ? ` of ${cycleSummary.totalLength}` : ''}
            </div>
            {cycleSummary.stage && (
              <div className="mt-1 text-sm text-pink-700 dark:text-pink-100">
                {cycleSummary.stage.name} • Day {cycleSummary.dayOfStage} of {cycleSummary.stage.length}
              </div>
            )}
            <button
              onClick={() => setRoute('settings')}
              className="mt-3 text-xs text-pink-700 dark:text-pink-200 underline"
            >
              Update cycle settings
            </button>
          </div>
        )}

        <div className="rounded-xl p-4 bg-gradient-to-r from-white to-gray-50 dark:from-dark-surface dark:to-dark-surfaceHover shadow-sm">
          <div className="text-sm text-gray-600 dark:text-dark-textSecondary mb-2">Snapshot</div>
          <div className={`${isMobile ? 'flex gap-3 overflow-x-auto pb-2' : 'grid grid-cols-2 gap-2 overflow-x-auto pb-1'}`}>
            {ELEMENTS.map((el) => (
              <div key={el.key} className={`${isMobile ? 'flex-none w-80' : 'flex-none'} rounded-lg p-3 border dark:border-dark-border bg-white dark:bg-dark-bg flex flex-col`}>
                <div className="flex items-center gap-2 flex-1">
                  <div className="text-xl">{el.emoji}</div>
                  <div className="flex-1">
                    <div className={`${isMobile ? 'text-base' : 'text-sm'} font-medium dark:text-dark-text`}>{el.label}</div>
                    <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-dark-textSecondary`}>{todayEntry?.values?.[el.key]?.text?.slice(0, 60) || 'No entry'}</div>
                    {todayEntry?.values?.[el.key]?.action && (
                      <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-blue-600 dark:text-blue-400 mt-1`}>
                        Action: {todayEntry.values[el.key].action}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRoute(`log-${el.key}`)}
                  className={`mt-2 w-full ${isMobile ? 'py-2 px-3 text-sm' : 'py-1 px-2 text-xs'} rounded transition-colors ${todayEntry?.values?.[el.key]?.text
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                    }`}
                >
                  {todayEntry?.values?.[el.key]?.text ? '✓ Update' : 'Log'}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <button onClick={() => setRoute("log-earth")} className="w-full py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600">Log Today</button>
            <button onClick={() => setRoute("previous-day")} className="w-full py-2 bg-gray-100 dark:bg-dark-surfaceHover text-gray-700 dark:text-dark-textSecondary rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-dark-border">
              Edit Yesterday
            </button>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-dark-textSecondary">Most Recent Entry</div>
            <button onClick={() => setRoute("insights")} className="text-sm text-blue-600 dark:text-blue-400">View All</button>
          </div>
          <div className="mt-2">
            {previousEntry ? (
              <div className="p-3 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium dark:text-dark-text">
                    {new Date(previousEntry.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-textSecondary">Previous</div>
                </div>
                <div className={`${isMobile ? 'flex gap-3 overflow-x-auto pb-2' : 'grid grid-cols-2 gap-2'}`}>
                  {ELEMENTS.map((el) => {
                    const elementData = previousEntry.values[el.key];
                    return (
                      <div key={el.key} className={`${isMobile ? 'flex-none w-80' : ''} flex flex-col p-2 rounded border dark:border-dark-border bg-gray-50 dark:bg-dark-bg`}>
                        <div className="flex items-start gap-2 flex-1">
                          <div className="text-sm">{el.emoji}</div>
                          <div className="flex-1 min-w-0">
                            <div className={`${isMobile ? 'text-sm' : 'text-xs'} font-medium text-gray-700 dark:text-dark-text`}>{el.label}</div>
                            <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-dark-textSecondary mt-1`}>
                              {elementData?.text ? (
                                <div className="truncate">{elementData.text}</div>
                              ) : (
                                <div className="text-gray-400 dark:text-dark-muted italic">No entry</div>
                              )}
                            </div>
                            {elementData?.action && (
                              <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-blue-600 dark:text-blue-400 mt-1`}>
                                Action: {elementData.action}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg border dark:border-dark-border bg-white dark:bg-dark-surface text-center">
                <div className="text-sm text-gray-500 dark:text-dark-textSecondary">No previous entries</div>
                <div className="text-xs text-gray-400 dark:text-dark-muted mt-1">Start logging to see your previous entries here</div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-dark-textSecondary">
          <div>Days since last entry: {daysSinceLastEntry(state.entries)}</div>
          <div className="mt-2">Theme: {state.theme}</div>
        </div>
      </div>
    </div>
  );
}

export default Home;
